import { useMemo } from "react";
import { useApiCache } from "../../../../../lib/useApiCache";
import {
	getQuotationRuntimeSharedConfigEndpoint,
	parseQuotationTemplatesConfig,
	QUOTATION_TEMPLATES_TOOL_SLUG,
	selectSummaryTemplateBody,
	type PaymentTemplateGroup,
} from "../../../../../lib/quotationTemplate/sharedConfig";

const CACHE_KEY = `tool-shared-config:${QUOTATION_TEMPLATES_TOOL_SLUG}`;
const CACHE_TTL_MS = 30 * 60 * 1000;

interface SharedConfigResponse {
	data?: { config?: unknown };
}

export interface QuotationTemplateRuntimeContext {
	categoriaItem?: number | null;
	paymentFlow?: PaymentTemplateGroup | null;
}

export interface UseQuotationTemplateConfigResult {
	config: ReturnType<typeof parseQuotationTemplatesConfig>["config"] | null;
	loading: boolean;
}

/**
 * Busca o template de mensagem (WhatsApp) da calculadora de orcamento no
 * banco do tenant, com cache local persistente + ETag. Quando nao ha
 * config salva (404) ou os dados sao invalidos, retorna body = null para
 * que o consumidor use o fallback hardcoded (DEFAULT_QUOTATION_SUMMARY_BODY).
 */
export function useQuotationTemplateConfig(): UseQuotationTemplateConfigResult {
	const { data, loading } = useApiCache<SharedConfigResponse>(
		CACHE_KEY,
		getQuotationRuntimeSharedConfigEndpoint(),
		{
			area: "local",
			useETag: true,
			ttlMs: CACHE_TTL_MS,
		},
	);

	const config = useMemo(() => {
		if (!data?.data?.config) return null;
		return parseQuotationTemplatesConfig(data.data.config).config;
	}, [data]);

	return { config, loading };
}

export function useQuotationSummaryTemplate(
	context: QuotationTemplateRuntimeContext,
): {
	templateBody: string | null;
	loading: boolean;
} {
	const { config, loading } = useQuotationTemplateConfig();

	const templateBody = useMemo(() => {
		return selectSummaryTemplateBody(config, {
			categoriaItem: context.categoriaItem,
			paymentFlow: context.paymentFlow,
		});
	}, [config, context.categoriaItem, context.paymentFlow]);

	return { templateBody, loading };
}
